import React, { useState, lazy, memo } from 'react';
import { AlertTriangle, Calendar, CheckCircle2, FlaskConical, Clock, ShieldAlert, Camera, Bell, Square, CheckSquare } from 'lucide-react';
interface BumpProps {
  checked: boolean;
  onToggle: () => void;
  name: string;
  image: string;
  headline: string;
  price: string;
  copy: string;
  features: {
    icon: React.ReactNode;
    text: string;
  }[];
  buttonText: string;
  accentColor: string;
}
function OrderBump({
  checked,
  onToggle,
  name,
  image,
  headline,
  price,
  copy,
  features,
  buttonText,
  accentColor
}: BumpProps) {
  return <div className={`border-2 rounded-2xl overflow-hidden transition-all duration-300 text-left ${checked ? `border-gold bg-gold/5` : 'border-charcoal/10 bg-cream/50'}`} data-id="element-120">
      <div className={`px-4 py-3 ${checked ? 'bg-gold/10' : 'bg-charcoal/5'}`} data-id="element-121">
        <h4 className="text-lg md:text-xl font-bold font-serif text-charcoal mb-1" data-id="element-122">
          {name}
        </h4>
        <div className="flex items-center gap-2" data-id="element-123">
          <AlertTriangle className="w-4 h-4 text-gold shrink-0" data-id="element-124" />
          <span className="font-medium text-sm text-charcoal/70" data-id="element-125">
            {headline}
          </span>
        </div>
      </div>

      <div className="p-5 md:p-6 space-y-4" data-id="element-126">
        <div className="flex flex-col sm:flex-row gap-5 items-start" data-id="element-127">
          <div className="w-full sm:w-1/3 shrink-0" data-id="element-128">
            <img src={image} alt={headline} className="w-full rounded-lg shadow-md" loading="lazy" decoding="async" width={300} height={225} data-id="element-129" />
          </div>
          <div className="flex-1 space-y-3" data-id="element-130">
            <p className="text-charcoal/70 text-sm leading-relaxed" data-id="element-131">{copy}</p>
            <ul className="space-y-2" data-id="element-132">
              {features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-133">
                  <span className="mt-0.5 shrink-0 text-gold" data-id="element-134">{f.icon}</span>
                  <span className="text-charcoal/80" data-id="element-135">{f.text}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <button onClick={onToggle} className={`w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border-2 transition-all duration-200 font-bold text-sm ${checked ? 'border-gold bg-gold text-charcoal shadow-lg' : 'border-gold/40 bg-gold/20 text-charcoal hover:bg-gold/30 hover:border-gold'}`} data-id="element-136">
          {checked ? <CheckSquare className="w-5 h-5" data-id="element-137" /> : <Square className="w-5 h-5" data-id="element-138" />}
          {buttonText}
        </button>
      </div>
    </div>;
}
interface OrderBumpsProps {
  bump1: boolean;
  setBump1: (v: boolean) => void;
  bump2: boolean;
  setBump2: (v: boolean) => void;
}
export function OrderBumps({
  bump1,
  setBump1,
  bump2,
  setBump2
}: OrderBumpsProps) {
  return <div className="space-y-4 w-full text-left mt-6 mb-8" data-id="element-139">
      <OrderBump checked={bump1} onToggle={() => setBump1(!bump1)} name="LA BIBLIA DE LAS MEZCLAS (Seguridad Total)" image="https://cdn.magicpatterns.com/uploads/pFnqeaMsPQXszi28qrj3Ye/pl-ob-1.png" headline="No arriesgues tu barrera cutánea (Solo $10.99)" price="$10.99" copy="¿Sabías que mezclar Retinol con Vitamina C en la misma noche puede causar quemaduras químicas silenciosas? La piel no lee el precio de la crema, lee el pH. Si aplicas los activos de farmacia en el orden incorrecto, podrías estar creando manchas en lugar de quitarlas." features={[{
      icon: <FlaskConical className="w-4 h-4" data-id="element-141" />,
      text: 'El Semáforo de la Química: Tabla visual de mezclas permitidas y prohibidas.'
    }, {
      icon: <Clock className="w-4 h-4" data-id="element-142" />,
      text: 'Guía de Tiempos: Cuántos minutos esperar exactamente entre cada capa.'
    }, {
      icon: <ShieldAlert className="w-4 h-4" data-id="element-143" />,
      text: 'Protocolo SOS: Qué hacer en 24h si tu piel reacciona mal.'
    }]} buttonText="SÍ, QUIERO SEGURIDAD TOTAL POR $10.99" accentColor="gold" data-id="element-140" />

      <OrderBump checked={bump2} onToggle={() => setBump2(!bump2)} name="SKIN-TRACKER PRO (Disciplina de Lujo)" image="https://cdn.magicpatterns.com/uploads/bhcKtikC5eJdgc4EnYcDS5/pl-ob-2.png" headline="Asegura tus resultados: El Mapa de 28 Días (Solo $9.99)" price="$9.99" copy="El mayor enemigo de una piel de porcelana no es la genética, es la falta de constancia. El Skin-Cycling 2-2 solo funciona si sigues el ritmo. No confíes en tu memoria; confía en un sistema diseñado por expertos para que nunca te saltes una noche de recuperación." features={[{
      icon: <Calendar className="w-4 h-4" data-id="element-145" />,
      text: 'Calendario Interactivo (PDF/Notion): Marca tus avances cada noche.'
    }, {
      icon: <Camera className="w-4 h-4" data-id="element-146" />,
      text: 'Guía de Fotos de Progreso: Cómo detectar micro-cambios en tu textura.'
    }, {
      icon: <Bell className="w-4 h-4" data-id="element-147" />,
      text: 'Recordatorio de Re-Stock: Cuándo comprar tus genéricos antes de que se agoten.'
    }]} buttonText="SÍ, QUIERO EL TRACKER DE RESULTADOS POR $9.99" accentColor="gold" data-id="element-144" />
    </div>;
}